/**
 * Мини-markdown → HTML парсер. Достаточен для FAQ-статей админ-панели.
 *
 * Поддерживает:
 *   - заголовки # ## ### #### #####
 *   - **bold**, *italic*, `code`
 *   - [текст](ссылка)
 *   - ![alt](url) — картинки
 *   - - элементы списка
 *   - GitHub-style таблицы (| col | col | + строка-разделитель)
 *   - > blockquote (одна строка)
 *   - переносы строк
 *
 * Не поддерживает: вложенные списки, кодовые блоки ```.
 * Если когда-нибудь понадобится — поставить marked + DOMPurify через npm.
 *
 * Безопасность: HTML-входов в content быть не должно (мы сами эскейпим <),
 * но если когда-нибудь в content попадёт <script> — он будет неактивен,
 * так как мы рендерим через dangerouslySetInnerHTML только сгенерированный
 * нами HTML с уже эскейпленным '<'.
 */

const ESCAPE_HTML = (s) => s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

// Inline-преобразования (внутри параграфа)
function inline(text) {
    let html = ESCAPE_HTML(text);
    // Картинки ![alt](url) — раньше ссылок, чтобы не пересеклись
    html = html.replace(/!\[([^\]]*?)\]\(([^)]+?)\)/g,
        (_, alt, url) => `<img src="${url}" alt="${alt}" loading="lazy" />`);
    // Ссылки [текст](url)
    html = html.replace(/\[([^\]]+?)\]\(([^)]+?)\)/g,
        (_, text, url) => `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`);
    // **bold**
    html = html.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>');
    // *italic* — после **, чтобы не пересеклись
    html = html.replace(/(^|[^*])\*([^*\n]+?)\*(?!\*)/g, '$1<em>$2</em>');
    // `code`
    html = html.replace(/`([^`\n]+?)`/g, '<code>$1</code>');
    return html;
}


// Парсит строку таблицы "| a | b | c |" → массив ["a", "b", "c"].
// Внешние пайпы и пробелы по краям убираем.
function parseTableRow(line) {
    let s = line.trim();
    if (s.startsWith('|')) s = s.slice(1);
    if (s.endsWith('|'))   s = s.slice(0, -1);
    return s.split('|').map(c => c.trim());
}

// Проверяет, что строка — это разделитель таблицы:
// "| --- | --- |" или "|---|:---:|---:|"
function isTableDivider(line) {
    const s = line.trim();
    if (!s.includes('|')) return false;
    return /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?$/.test(s);
}


export function mdToHtml(md) {
    if (!md) return '';

    const lines = md.split(/\r?\n/);
    const html = [];
    let inList = false;
    let inPara = [];

    function flushPara() {
        if (inPara.length) {
            html.push('<p>' + inPara.map(inline).join('<br/>') + '</p>');
            inPara = [];
        }
    }
    function flushList() {
        if (inList) {
            html.push('</ul>');
            inList = false;
        }
    }

    for (let i = 0; i < lines.length; i++) {
        const raw = lines[i];
        const line = raw.trim();
        if (!line) {
            flushPara();
            flushList();
            continue;
        }

        // Заголовки
        const h = line.match(/^(#{1,5})\s+(.*)$/);
        if (h) {
            flushPara();
            flushList();
            const level = h[1].length;
            html.push(`<h${level}>${inline(h[2])}</h${level}>`);
            continue;
        }

        // Таблица: текущая строка с | + следующая — divider
        if (line.includes('|') && i + 1 < lines.length && isTableDivider(lines[i + 1])) {
            flushPara();
            flushList();
            const headers = parseTableRow(line);
            i++; // пропускаем divider
            const rows = [];
            while (i + 1 < lines.length) {
                const next = lines[i + 1].trim();
                if (!next || !next.includes('|')) break;
                rows.push(parseTableRow(next));
                i++;
            }
            html.push('<table>');
            html.push('<thead><tr>' + headers.map(c => `<th>${inline(c)}</th>`).join('') + '</tr></thead>');
            if (rows.length) {
                html.push('<tbody>');
                for (const r of rows) {
                    html.push('<tr>' + r.map(c => `<td>${inline(c)}</td>`).join('') + '</tr>');
                }
                html.push('</tbody>');
            }
            html.push('</table>');
            continue;
        }

        // Blockquote (однострочный, для коротких выделений)
        if (/^>\s+/.test(line)) {
            flushPara();
            flushList();
            html.push(`<blockquote>${inline(line.replace(/^>\s+/, ''))}</blockquote>`);
            continue;
        }

        // Список
        if (/^[-*]\s+/.test(line)) {
            flushPara();
            if (!inList) { html.push('<ul>'); inList = true; }
            const item = line.replace(/^[-*]\s+/, '');
            html.push(`<li>${inline(item)}</li>`);
            continue;
        }

        flushList();
        inPara.push(line);
    }
    flushPara();
    flushList();

    return html.join('\n');
}
